import dayjs from 'dayjs'
import { z } from 'zod'
import { accountDisplaySchema } from '../profile/contract.ts'

export { customerSendPermissions, type CustomerSendInput } from './customer-send-contract.ts'
export { type CalendarSendInput, type CalendarSendResult } from './calendar-send-contract.ts'

export type { CustomerIssueInput, CustomerIssuePreview } from './customer-issue-contract.ts'

const id = z.number().int().positive()
export const billStatusSchema = z.enum(['draft', 'issued', 'voided'])
export const billCollectionStatusSchema = z.enum([
  'notIssued',
  'voided',
  'notRequired',
  'unpaid',
  'partial',
  'paid',
  'refundDue'
])
export const feeTypeSchema = z.enum([
  'rent',
  'water',
  'electricity',
  'propertyManagement',
  'service',
  'deposit',
  'commission',
  'outsourcing',
  'salary',
  'reimbursement',
  'repair',
  'office',
  'passThrough',
  'other'
])
// 金额与单价传十进制字符串，使用 Decimal 计算并统一保留两位小数。
export const billMoneySchema = z
  .string()
  .regex(/^(0|[1-9]\d{0,15})(\.\d{1,2})?$/, '金额应为非负十进制字符串，最多两位小数')
export const billQuantitySchema = z.string().regex(/^(0|[1-9]\d{0,13})(\.\d{1,4})?$/, '用量应为非负数，最多四位小数')
export const billingMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, '归属月份格式应为 YYYY-MM')
const date = z.iso.date()
const periods = {
  periodStart: date.nullable().default(null),
  periodEnd: date.nullable().default(null)
}
function validPeriod(value: { periodStart: string | null; periodEnd: string | null }) {
  return (
    (value.periodStart === null && value.periodEnd === null) ||
    (value.periodStart !== null && value.periodEnd !== null && value.periodStart <= value.periodEnd)
  )
}
export const billSourceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('rental'), orderId: id }),
  z.object({ type: z.literal('service'), orderId: id })
])
const draftFields = {
  expenseBusinessId: id.nullable().optional(),
  paymentDetails: z.string().trim().max(5000).nullable().optional(),
  expenseUrgent: z.boolean().optional(),
  ...periods,
  dueAt: date.nullable().default(null),
  note: z.string().trim().max(5000).nullable().default(null),
  feeType: feeTypeSchema,
  description: z.string().trim().min(1).max(255),
  originalAmount: billMoneySchema.nullable(),
  quantity: billQuantitySchema.nullable().default(null),
  unitPrice: billMoneySchema.nullable().default(null)
}
export const billCreateSchema = z
  .object({
    source: billSourceSchema.nullable().default(null),
    direction: z.enum(['receivable', 'payable']).default('receivable'),
    organizationId: id.optional(),
    payeeId: id.optional(),
    siteId: id.optional(),
    businessId: id.optional(),
    salespersonId: id.optional(),
    idempotencyKey: z.uuid(),
    ...draftFields
  })
  .refine(validPeriod, { message: '账期必须同时填写，且结束日期不早于开始日期', path: ['periodEnd'] })
export const billUpdateSchema = z
  .object({
    billId: id,
    expectedUpdatedAt: z.date(),
    reason: z.string().trim().min(1).max(1000).optional(),
    ...draftFields
  })
  .refine(validPeriod, { message: '账期必须同时填写，且结束日期不早于开始日期', path: ['periodEnd'] })
export const billIdSchema = z.object({ billId: id })
export const billIssueSchema = billIdSchema.extend({ expectedUpdatedAt: z.date() })
export const billVoidSchema = billIssueSchema.extend({ reason: z.string().trim().min(1).max(1000) })
export const billListSchema = z
  .object({
    current: id.default(1),
    pageSize: id.max(100).default(20),
    id: id.optional(),
    keyword: z.string().trim().max(255).optional(),
    note: z.string().trim().max(255).optional(),
    creatorMemberId: id.optional(),
    customerId: z.union([id, z.array(id).max(100)]).optional(),
    organizationId: z.union([id, z.array(id).max(100)]).optional(),
    salespersonId: z
      .union([z.number().int().nonnegative(), z.array(z.number().int().nonnegative()).max(100)])
      .optional(),
    businessId: z.union([z.number().int().nonnegative(), z.array(z.number().int().nonnegative()).max(100)]).optional(),
    siteId: z.number().int().nonnegative().optional(),
    orderType: z.enum(['rental', 'service']).optional(),
    orderGroupId: id.optional(),
    rentalOrderId: id.optional(),
    serviceOrderId: id.optional(),
    archived: z.enum(['active', 'archived', 'all']).optional(),
    status: z.union([billStatusSchema, z.array(billStatusSchema).max(100)]).optional(),
    collectionStatus: z.union([billCollectionStatusSchema, z.array(billCollectionStatusSchema).max(100)]).optional(),
    direction: z.enum(['receivable', 'payable']).optional(),
    feeType: feeTypeSchema.optional(),
    settlement: z.enum(['outstanding', 'refundable', 'settled', 'unknown', 'notIssued', 'voided']).optional(),
    grouped: z.enum(['yes', 'no']).optional(),
    periodFrom: date.optional(),
    periodTo: date.optional(),
    payeeId: id.optional(),
    expenseBusinessId: id.optional(),
    expenseUrgent: z.boolean().optional(),
    createdFrom: z.iso.datetime({ offset: true }).optional(),
    createdTo: z.iso.datetime({ offset: true }).optional(),
    receivedFrom: z.iso.datetime({ offset: true }).optional(),
    receivedTo: z.iso.datetime({ offset: true }).optional(),
    sortField: z.enum(['createdAt', 'dueAt']).default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc')
  })
  .refine((v) => !v.createdFrom || !v.createdTo || !dayjs(v.createdFrom).isAfter(v.createdTo), '创建时间区间无效')
  .refine((v) => !v.receivedFrom || !v.receivedTo || !dayjs(v.receivedFrom).isAfter(v.receivedTo), '收付时间区间无效')

export const billFilterOptionsInputSchema = z.object({
  purpose: z.enum(['bill.read', 'bill.group.read', 'money.read']),
  kind: z.enum(['customer', 'salesperson', 'organization', 'business']),
  keyword: z.string().trim().max(80).optional(),
  selectedId: id.optional(),
  selectedIds: z.array(id).max(100).optional()
})
export const billFilterOptionSchema = z.object({ value: id, label: z.string() })

export const billSummarySchema = z.object({
  billedAmount: z.string(),
  receivedAmount: z.string(),
  refundedAmount: z.string(),
  outstandingAmount: z.string(),
  refundableAmount: z.string()
})
export const billSchema = z.object({
  expenseBusinessId: id.nullable().optional(),
  expenseBusinessName: z.string().nullable().optional(),
  expenseBusinessColor: z.string().nullable().optional(),
  paymentDetails: z.string().nullable().optional(),
  expenseUrgent: z.boolean().optional(),
  creator: accountDisplaySchema.nullish(),
  id,
  billNo: z.string(),
  customerId: id.nullable(),
  payeeId: id.nullable(),
  direction: z.enum(['receivable', 'payable']),
  feeType: feeTypeSchema,
  description: z.string(),
  originalAmount: z.string().nullable(),
  quantity: z.string().nullable(),
  unitPrice: z.string().nullable(),
  meteredUnitPrice: billMoneySchema.nullable().optional(),
  version: id,
  rentalChargeRuleId: id.nullable(),
  serviceChargeRuleId: id.nullable().default(null),
  requiredForActivation: z.boolean(),
  customerName: z.string(),
  organizationId: id,
  organizationName: z.string(),
  rentalOrderId: id.nullable(),
  serviceOrderId: id.nullable(),
  orderType: z.enum(['rental', 'service']).nullable(),
  sourceOrderNo: z.string().nullable(),
  businessName: z.string().nullable(),
  businessColor: z.string().nullable(),
  salespersonName: z.string().nullable(),
  status: billStatusSchema,
  collectionStatus: billCollectionStatusSchema,
  latestSettlementAt: z.date().nullable(),
  periodStart: z.date().nullable(),
  periodEnd: z.date().nullable(),
  archivedAt: z.date().nullable().optional(),
  issuedAt: z.date().nullable(),
  dueAt: z.date().nullable(),
  note: z.string().nullable(),
  totalAmount: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  ...billSummarySchema.shape
})
export const billDetailSchema = billSchema.extend({
  calculationSnapshot: z.unknown().nullable(),
  adjustments: z.array(
    z.object({ id, kind: z.string(), amount: z.string(), reason: z.string(), effectiveAt: z.date() })
  ),
  allocations: z.array(
    z.object({
      id,
      transactionId: id,
      amount: z.string(),
      note: z.string().nullable(),
      kind: z.enum(['receipt', 'receiptRefund', 'payment', 'paymentRefund']),
      status: z.enum(['pending', 'confirmed', 'voided']),
      occurredAt: z.date(),
      transactionNote: z.string().nullable(),
      attachmentIds: z.array(id)
    })
  )
})
export const billListOutputSchema = z.object({
  data: z.array(billSchema),
  total: z.number().int().nonnegative(),
  current: id,
  pageSize: id
})
export type BillCreateInput = z.infer<typeof billCreateSchema>
export type BillUpdateInput = z.infer<typeof billUpdateSchema>
export type BillListInput = z.infer<typeof billListSchema>
export type BillIssueInput = z.infer<typeof billIssueSchema>
export type BillVoidInput = z.infer<typeof billVoidSchema>
export type BillFilterOptionsInput = z.infer<typeof billFilterOptionsInputSchema>

export const BILL_SEARCH_BATCH_ASYNC_THRESHOLD = 200

// 小批量保留逐张确认；大批量由服务端固定快照后入队。
export const billBatchTargetSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('selected'),
    ids: z.array(id).min(1).max(100),
    direction: z.enum(['receivable', 'payable']).optional()
  }),
  z.object({ mode: z.literal('search'), search: billListSchema })
])
export const billBatchActionSchema = z
  .object({
    action: z.enum(['issue', 'archive', 'amount', 'note', 'delete']),
    bills: z
      .array(
        billIssueSchema.extend({
          version: id,
          targetAmount: billMoneySchema.optional(),
          issueAmount: billMoneySchema.optional(),
          issueQuantity: billQuantitySchema.optional(),
          issueUnitPrice: billMoneySchema.optional()
        })
      )
      .min(1)
      .max(BILL_SEARCH_BATCH_ASYNC_THRESHOLD)
      .refine((items) => new Set(items.map((item) => item.billId)).size === items.length, '账单不能重复'),
    amount: billMoneySchema.optional(),
    reason: z.string().trim().min(1).max(1000).optional(),
    note: z.string().trim().min(1, '请填写备注').max(5000).optional(),
    idempotencyKey: z.uuid()
  })
  .superRefine((value, ctx) => {
    if (value.bills.some((item) => item.issueAmount !== undefined && item.issueQuantity !== undefined)) {
      ctx.addIssue({ code: 'custom', message: '用量计算和手工金额只能选择一种', path: ['bills'] })
    }
    if (value.bills.some((item) => item.issueUnitPrice !== undefined && item.issueQuantity === undefined)) {
      ctx.addIssue({ code: 'custom', message: '修改单价时必须同时填写用量', path: ['bills'] })
    }
    if (
      value.action !== 'issue' &&
      value.bills.some(
        (item) =>
          item.issueAmount !== undefined || item.issueQuantity !== undefined || item.issueUnitPrice !== undefined
      )
    ) {
      ctx.addIssue({ code: 'custom', message: '仅确认出账支持逐张填写金额', path: ['bills'] })
    }
    if (
      value.action === 'amount' &&
      (!value.reason || value.bills.some((item) => item.targetAmount === undefined && value.amount === undefined))
    ) {
      ctx.addIssue({ code: 'custom', message: '修改金额必须填写目标金额及原因' })
    }
    if (value.action === 'note' && !value.note) {
      ctx.addIssue({ code: 'custom', message: '请填写备注', path: ['note'] })
    }
  })
export type BillBatchActionInput = z.infer<typeof billBatchActionSchema>

export const billSearchBatchOperationSchema = z
  .object({
    action: billBatchActionSchema.shape.action,
    amount: billMoneySchema.optional(),
    reason: z.string().trim().min(1).max(1000).optional(),
    note: z.string().trim().min(1).max(5000).optional(),
    idempotencyKey: z.uuid()
  })
  .superRefine((value, ctx) => {
    if (value.action === 'amount' && (value.amount === undefined || !value.reason)) {
      ctx.addIssue({ code: 'custom', message: '修改金额必须填写统一目标金额及原因' })
    }
    if (value.action === 'note' && !value.note) {
      ctx.addIssue({ code: 'custom', message: '请填写备注', path: ['note'] })
    }
  })
export const billSearchBatchSchema = billSearchBatchOperationSchema.safeExtend({
  search: billListSchema,
  expectedCount: z.number().int().positive()
})
export type BillSearchBatchInput = z.infer<typeof billSearchBatchSchema>
export const billBatchResultsSchema = z.array(
  z.object({ billId: id, success: z.boolean(), message: z.string().optional() })
)
export const billSearchBatchStatusSchema = z.object({
  state: z.enum(['waiting', 'active', 'completed', 'failed', 'missing']),
  total: z.number().int().nonnegative(),
  processed: z.number().int().nonnegative(),
  results: billBatchResultsSchema,
  message: z.string().optional()
})
